import { makeAutoObservable, runInAction } from 'mobx'
import { omit } from 'lodash'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseInbox, type IParamsCreateInbox } from 'entities/Inbox/api/types'
import { PhoneApi } from 'entities/Phone'
import { type IParamsNumbersLandlineTeamCreate } from 'entities/Phone/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import { ComplianceApi } from 'entities/Compliance'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { type SettingsStore } from 'pages/settings/store/settingsStore'
import { type IAddNumberType, resolveInitialAddNumberType } from 'pages/settings/lib/addNumberType'
import { NewInboxStepsCommonStore } from 'pages/settings/store/newInboxSteps/newInboxCommonStepsStore'
import { TextEnableLandlineStore } from './textEnableLandlineStore'

export enum NewInboxRoutesSteps {
  NewInbox = 'new-inbox',
  HowAddNumber = 'how-add-number',
  AddSalesmsgNumbers = 'add-salesmsg-numbers',
  Payment = 'payment',
  ChooseNumber = 'choose-number',
  SelectAppAndNumbers = 'select-app-and-numbers',
  EnterNumber = 'enter-number',
  EnterAddress = 'enter-address',
  VerifyNumber = 'verify-number',
  AuthorizationDocument = 'authorization-document',
}

const titleMap: Record<string, string> = {
  [NewInboxRoutesSteps.NewInbox]: 'New inbox',
  [NewInboxRoutesSteps.HowAddNumber]: 'How do you want to add a number?',
  [NewInboxRoutesSteps.AddSalesmsgNumbers]: 'Add Salesmsg numbers',
  [NewInboxRoutesSteps.Payment]: 'Payment',
  [NewInboxRoutesSteps.ChooseNumber]: 'Choose number',
  [NewInboxRoutesSteps.SelectAppAndNumbers]: 'Select app & numbers',
  [NewInboxRoutesSteps.EnterNumber]: 'Enter number',
  [NewInboxRoutesSteps.EnterAddress]: 'Enter address',
  [NewInboxRoutesSteps.VerifyNumber]: 'Verify number',
  [NewInboxRoutesSteps.AuthorizationDocument]: 'Authorization Document (LOA)',
}

export class NewInboxStepsStore {
  private _currentStep = 1
  private _stepLoading = false
  private _addNumberType: IAddNumberType = 'assign'
  private _shouldShowPaymentStep = false

  readonly commonStore: NewInboxStepsCommonStore
  private _textEnableLandlineStore: TextEnableLandlineStore

  constructor(private _settingsContext: SettingsStore, private _entities: IEntitiesStore) {
    this.commonStore = new NewInboxStepsCommonStore(this._entities, {
      nextClickHandler: () => {
        this._shouldShowPaymentStep = true
        this.nextClickHandler()
      },
      onPaymentNotRequired: () => {
        this.createInbox()
      },
      getAddNumberType: () => this._addNumberType,
      getSelectedNumbersCount: () => this.selectedNumbersCount,
    })
    this._textEnableLandlineStore = new TextEnableLandlineStore(this, this._entities)

    makeAutoObservable(this)
  }

  init = async () => {
    await this.commonStore.init()
    runInAction(() => {
      this._addNumberType = resolveInitialAddNumberType(
        this.commonStore.unassignedNumbersCount,
        this.commonStore.isGracePeriod
      )
    })
  }

  get generalInfoStore() {
    return this.commonStore.newInboxStore
  }

  get addSalesmsgNumbersStore() {
    return this.commonStore.addSalesmsgNumbersStore
  }

  get selectAppAndNumbersStore() {
    return this.commonStore.selectAppAndNumbersStore
  }

  get paymentStore() {
    return this.commonStore.paymentStore
  }

  get chooseNumberStore() {
    return this.commonStore.chooseNumberStore
  }

  get textEnableLandlineStore() {
    return this._textEnableLandlineStore
  }

  get selectedNumbersCount() {
    if (this.commonStore.isBuyingNumber) {
      return this.chooseNumberStore.chooseNumberTabsStore.selectedNumber ? 1 : 0
    }
    if (this._addNumberType === 'app') {
      return this.selectAppAndNumbersStore.selectedNumber ? 1 : 0
    }
    if (this._addNumberType === 'landline') {
      return this.textEnableLandlineStore.phone ? 1 : 0
    }
    return this.addSalesmsgNumbersStore.numbersStore.multiSelectedValues.length
  }

  get rootPath() {
    return InboxesPath.addInbox.steps
  }

  get stepMap(): Record<number, NewInboxRoutesSteps> {
    const defaultStepMap: Record<number, NewInboxRoutesSteps> = {
      1: NewInboxRoutesSteps.NewInbox,
      2: NewInboxRoutesSteps.HowAddNumber,
      3: NewInboxRoutesSteps.AddSalesmsgNumbers,
    }

    switch (this._addNumberType) {
      case 'assign': {
        const stepMap = { ...defaultStepMap }
        if (this._shouldShowPaymentStep) {
          stepMap[4] = NewInboxRoutesSteps.Payment
        }
        return stepMap
      }
      case 'buy': {
        const stepMap = { ...defaultStepMap }
        stepMap[3] = NewInboxRoutesSteps.ChooseNumber
        stepMap[4] = NewInboxRoutesSteps.Payment
        return stepMap
      }
      case 'app': {
        const stepMap = { ...defaultStepMap }
        stepMap[3] = NewInboxRoutesSteps.SelectAppAndNumbers
        stepMap[4] = NewInboxRoutesSteps.Payment
        return stepMap
      }
      case 'landline': {
        const stepMap = { ...defaultStepMap }
        stepMap[3] = NewInboxRoutesSteps.EnterNumber
        stepMap[4] = NewInboxRoutesSteps.EnterAddress
        stepMap[5] = NewInboxRoutesSteps.VerifyNumber
        stepMap[6] = NewInboxRoutesSteps.AuthorizationDocument
        stepMap[7] = NewInboxRoutesSteps.Payment
        return stepMap
      }
      default:
        return defaultStepMap
    }
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get getCurrentUrl() {
    return `${this.rootPath}/${this.getCurrentStep}`
  }

  get getNextUrl() {
    return `${this.rootPath}/${this.getNextStep}`
  }

  get getLastUrl() {
    return `${this.rootPath}/${this.getLastStep}`
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  get stepLoading() {
    return this._stepLoading || this.commonStore.stepLoading
  }

  get hideBack() {
    return this.getCurrentStep === NewInboxRoutesSteps.VerifyNumber
  }

  get addNumberType() {
    return this._addNumberType
  }

  setAddNumberType = (type: IAddNumberType) => {
    this._addNumberType = type
    this._shouldShowPaymentStep = false
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: this.getNextUrl,
      query: uiStore.location?.search,
    })
  }

  redirectToRoot = () => {
    uiStore.changeRouteViaVue({
      path: `/${InboxesPath.root}`,
    })
  }

  decreaseStep = () => {
    if (this.getCurrentStep === NewInboxRoutesSteps.Payment) {
      this._shouldShowPaymentStep = false
    }

    if (this.getLastStep === NewInboxRoutesSteps.HowAddNumber) {
      this.resetStores()
    }

    uiStore.changeRoute({
      path: this.getLastUrl,
      query: uiStore.location?.search,
    })
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1

    return allSteps[currentPageIndex + direction]
  }

  getSettingsStep = (path: NewInboxRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getStepByPath = (newInboxRoutes: NewInboxRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === newInboxRoutes
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  nextStepByNumber = (number: number, query?: string) => {
    this.setAddNumberType('landline')
    const step = this.stepMap[number]

    uiStore.changeRoute({
      path: `${this.rootPath}/${step}`,
      query: query || uiStore.location?.search,
    })
  }

  createInbox = async () => {
    this.paymentStore.setPaymentError(false)
    const params = {
      name: this.commonStore.newInboxStore.newInboxName,
      owner: { id: Number(this.commonStore.newInboxStore.activeInboxOwner) },
      members: this.commonStore.newInboxStore.selectedMembersIds,
      number_vendor_id: this._entities.complianceStore.numberVendorId,
    }

    try {
      switch (this._addNumberType) {
        case 'assign':
          return await this.commonStore.assignToNewInbox(params, {
            onSuccess: this.successInboxCreated,
          })
        case 'buy':
          return await this.commonStore.buyNumberAndCreateInbox(params, {
            onSuccess: this.successInboxCreated,
          })
        case 'app':
          return await this.commonStore.createInboxWithAppNumber(params, {
            onSuccess: this.successInboxCreated,
          })
        case 'landline':
          return await this.createInboxWithLandlineNumber(params)
        default:
          return null
      }
    } catch (error) {
      logger.error(error)
    }
  }

  successInboxCreated = async (inbox: IResponseInbox) => {
    toastStore.add({
      type: 'success',
      title: 'New inbox created',
    })

    const phone = inbox.number
    const addUseCaseLater = this.chooseNumberStore.selectedUseCaseId === -1
    const noUseCasesYet = this.chooseNumberStore.useCases.length === 0
    const noCompletedUseCases = this.chooseNumberStore.completedUseCases.length === 0
    if (!phone) return

    const needVerify =
      phone.is_toll_free && (addUseCaseLater || noUseCasesYet || noCompletedUseCases)
    if (needVerify) {
      this._entities.complianceStore.setUnverifiedTollFreeNumber(new Phone(phone, this._entities))
    }

    const { selectedUseCaseId, useCaseNumberIds } = this.chooseNumberStore

    if (selectedUseCaseId !== -1 && selectedUseCaseId) {
      await ComplianceApi.submitTollFreeForReview(selectedUseCaseId, {
        numbers: useCaseNumberIds ? [...useCaseNumberIds, phone.id] : [phone.id],
        additional_numbers: [phone.id],
      })
    }

    uiStore.changeRouteViaVue({
      path: InboxesPath.inboxNumbers(inbox.id),
    })

    if (needVerify && !this._entities.organizationStore.isTwilioNumberVendor) {
      setTimeout(() => {
        this._settingsContext.openVerifyNumberModal(phone, inbox.name || '')
      }, 500)
    }
  }

  failureLandlineCreated = () => {
    toastStore.add({
      type: 'error',
      title: 'Landline is not added',
      desc: 'This landline number couldn’t be added to the inbox',
    })
    uiStore.changeRouteViaVue({
      path: InboxesPath.root,
    })
  }

  createInboxWithLandlineNumber = async (
    params: Omit<IParamsCreateInbox, 'number' | 'vendor_id'>
  ) => {
    try {
      runInAction(() => {
        this._stepLoading = true
      })
      const paramsWithoutNumberVendorId = omit(params, ['number_vendor_id'])

      const payload: IParamsNumbersLandlineTeamCreate = {
        ...paramsWithoutNumberVendorId,
        members: this.commonStore.newInboxStore.selectedMembersIds.map((id) => Number(id)),
        phone: this._textEnableLandlineStore.payload.phone,
      }

      const response = await PhoneApi.createNumbersLandlineTeamCreate(payload)
      const data = response.data

      if ('error' in data) {
        logger.error(data.error)
        this.failureLandlineCreated()

        return
      } else {
        toastStore.add({
          type: 'success',
          title: 'Landline is in progress to be text-enabled',
          desc: 'Sign the LOA to complete the process',
        })
        uiStore.changeRouteViaVue({
          path: InboxesPath.inboxNumbers(data.id),
        })
      }
    } catch (error) {
      logger.error(error)
      this.failureLandlineCreated()
    } finally {
      runInAction(() => {
        this._stepLoading = false
      })
    }
  }

  resetStores = () => {
    this.commonStore.resetStores()
    this._textEnableLandlineStore.dispose()
  }
}
